import React, {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../context/AuthContext";
import {api} from "../../services/apiService";

export default function Account() {
    const navigate = useNavigate();
    const {user, logout, isAdmin} = useAuth();
    const [loading, setLoading] = useState(false);
    const [passwordError, setPasswordError] = useState("");

    const [profile, setProfile] = useState({
        firstName: "",
        lastName: "",
        email: ""
    });

    const [passwords, setPasswords] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    const handleProfileChange = ({target}) => {
        setProfile(prev => ({...prev, [target.name]: target.value}));
    };

    const handlePasswordChange = ({target}) => {
        setPasswords(prev => ({...prev, [target.name]: target.value}));
    };

    const handleSaveProfile = () => {
        if (profile.email === user.email) {
            return;
        }

        if (!/\S+@\S+\.\S+/.test(profile.email) && !isAdmin()) {
            alert("Veuillez entrer une adresse e-mail valide.");
            return;
        }

        api.post("auth/update_mail", {
            newEmail: profile.email
        })
            .then(res => {
                // Profile updated successfully
                user.email = profile.email;
            })
            .catch(error => {
                console.error("Erreur lors de la mise à jour du profil :", error);
                alert(error.response.data?.message || "Une erreur est survenue lors de la mise à jour du profil.");
            });
    };

    const handleUpdatePassword = () => {
        if (passwords.newPassword !== passwords.confirmPassword) {
            setPasswordError("Les mots de passe ne correspondent pas.");
            return;
        }

        api.post("auth/update_password", {
            currentPassword: passwords.currentPassword,
            newPassword: passwords.newPassword
        })
            .then((response) => {
                setPasswords({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: ""
                });
                setPasswordError(null);
            })
            .catch((error) => {
                console.error("Erreur lors de la mise à jour du mot de passe :", error);
                setPasswordError(error.response?.data?.message);
            });
    };

    const handleLogout = async () => {
        const result = await logout();
        if (result.success) {
            navigate("/login");
        } else {
            alert(result.message);
        }
    };

    useEffect(() => {
        api.get(`auth/get_user_details/${user.id}`, handleProfileChange)
            .then((response) => {
                setProfile({
                    firstName: response.data.firstname,
                    lastName: response.data.lastname,
                    email: response.data.email
                });
            })
            .catch((error) => {
                console.error("Erreur lors de la récupération des détails de l'utilisateur :", error);
                alert("Une erreur est survenue lors de la récupération des détails de l'utilisateur.");
                navigate("/");
            })
            .finally(() => {
                setLoading(false);
            });

    }, [user, navigate])

    return (
        !loading ? (
                <Container maxWidth={"lg"}>
                    <Stack spacing={4}>
                        <Box>
                            <Typography variant="h1" gutterBottom>
                                Mon compte
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Consultez et ajustez vos informations personnelles.
                            </Typography>
                        </Box>

                        <Paper variant="outlined" sx={{p: 3}}>
                            <Typography variant="h6" gutterBottom>
                                Informations personnelles
                            </Typography>
                            <Stack spacing={2}>
                                <Stack direction="row" spacing={2}>
                                    <TextField
                                        label="Prénom"
                                        name="firstName"
                                        value={profile.firstName}
                                        onChange={handleProfileChange}
                                        fullWidth
                                        disabled
                                    />
                                    <TextField
                                        label="Nom"
                                        name="lastName"
                                        value={profile.lastName}
                                        onChange={handleProfileChange}
                                        fullWidth
                                        disabled
                                    />
                                </Stack>
                                <TextField
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={profile.email}
                                    onChange={handleProfileChange}
                                    fullWidth
                                />
                                <Button variant="contained" onClick={handleSaveProfile}>
                                    Enregistrer les modifications
                                </Button>
                            </Stack>
                        </Paper>

                        <Paper variant="outlined" sx={{p: 3}}>
                            <Typography variant="h6" gutterBottom>
                                Modifier le mot de passe
                            </Typography>

                            {/* Si on a password error */}
                            {passwordError && (
                                <Alert severity="error" sx={{mb: 2}}>
                                    {passwordError}
                                </Alert>
                            )}

                            <Stack spacing={2}>
                                <TextField
                                    label="Mot de passe actuel"
                                    name="currentPassword"
                                    type="password"
                                    value={passwords.currentPassword}
                                    onChange={handlePasswordChange}
                                    fullWidth
                                />
                                <TextField
                                    label="Nouveau mot de passe"
                                    name="newPassword"
                                    type="password"
                                    value={passwords.newPassword}
                                    onChange={handlePasswordChange}
                                    fullWidth
                                />
                                <TextField
                                    label="Confirmer le nouveau mot de passe"
                                    name="confirmPassword"
                                    type="password"
                                    value={passwords.confirmPassword}
                                    onChange={handlePasswordChange}
                                    fullWidth
                                />
                                <Button variant="outlined" onClick={handleUpdatePassword}>
                                    Mettre à jour le mot de passe
                                </Button>
                            </Stack>
                        </Paper>

                        <Divider/>

                        <Button variant="contained" color="error" onClick={handleLogout}>
                            Déconnexion
                        </Button>
                    </Stack>
                </Container>
            )
            :
            (
                <Container maxWidth="lg" sx={{
                    mt: 4,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "60vh"
                }}>
                    <Stack spacing={2} alignItems="center">
                        <Box>
                            <Typography variant="h6" sx={{mb: 2}}>Chargement...</Typography>
                            <Box sx={{display: "flex", justifyContent: "center"}}>
                                <CircularProgress color="primary"/>
                            </Box>
                        </Box>
                    </Stack>
                </Container>
            )
    );
}